import APIService from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { FormatCurrency } from "@/common/helpers"
import Pagination from "@/components/core/Pagination"
import Filter from "@/components/core/Filter/Filter"
import { AgentPortfolioItem } from "@/types/Agents.types"
import moment from "moment"
import { useCallback, useEffect, useState } from "react"
import { Badge, Card, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"

interface Props {
    ID: string
}

const QUANTITY_PER_PAGE = 20

const AgentCustomers = ({ ID }: Props) => {
    const { t } = useTranslation()
    const history = useHistory()
    const user = parsedUser()

    const [records, setRecords] = useState<AgentPortfolioItem[]>([])
    const [loading, setLoading] = useState(false)
    const [filterObj, setFilterObj] = useState<any>({})
    const [page, setPage] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [totalRecords, setTotalRecords] = useState(0)

    const bindData = useCallback(async (currentPage: number, search: string) => {
        if (!user?.OrganizationId || !ID) return

        try {
            setLoading(true)
            const request = await APIService.getAction(
                "agents/Portfolio",
                `OrganizationId=${user.OrganizationId}&Id=${ID}&Search=${encodeURIComponent(search || "")}&Page=${currentPage}&Quantity=${QUANTITY_PER_PAGE}`
            )
            if (request.data.status === "ok") {
                setRecords(request.data.response || [])
                setTotalPages(request.data.quantityOfPages ?? 0)
                setTotalRecords(request.data.quantityOfRecords ?? 0)
            }
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }, [user?.OrganizationId, ID])

    const getFilterValues = (obj: any) => {
        setFilterObj(obj)
        setPage(0)
        bindData(0, obj.search)
    }

    useEffect(() => {
        bindData(page, filterObj.search)
    }, [ID])

    return (
        <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white border-bottom d-flex align-items-center justify-content-between">
                <h6 className="mb-0">
                    <i className="fa fa-address-book me-2 text-primary" />
                    {t("Customer Portfolio")}
                    <small className="text-muted ms-2">{totalRecords}</small>
                </h6>
                {loading && <Spinner animation="border" size="sm" variant="primary" />}
            </Card.Header>
            <Card.Body className="p-0">
                <div className="px-3 pt-2">
                    <Filter
                        fields={[
                            {
                                name: "search",
                                label: t("Search"),
                                className: "col-12 col-md-4",
                                htmlType: "text",
                            },
                        ]}
                        filterAction={() => bindData(0, filterObj.search)}
                        setFilterFields={getFilterValues}
                    />
                </div>
                <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th className="ps-3">{t("Client")}</th>
                                <th>{t("Status")}</th>
                                <th className="text-center">{t("Invoices")}</th>
                                <th className="text-end">{t("Total Invoiced")}</th>
                                <th>{t("Last Invoice")}</th>
                                <th className="text-end">{t("Unpaid Invoices")}</th>
                                <th className="pe-3">{t("Place")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {records.map(record => (
                                <tr key={record.clientCode}>
                                    <td className="ps-3">
                                        <span
                                            className="clickable"
                                            onClick={() => history.push(`/search?search=${encodeURIComponent(record.clientName || record.clientCode)}`)}
                                        >
                                            <div className="fw-semibold text-primary">{record.clientName}</div>
                                            <small className="text-muted">
                                                {record.clientCode}
                                                {record.classification && ` · ${record.classification}`}
                                            </small>
                                        </span>
                                        {record.phone && <div><small className="text-muted">{record.phone}</small></div>}
                                    </td>
                                    <td>
                                        {record.status && <Badge bg="light" text="dark">{record.status}</Badge>}
                                    </td>
                                    <td className="text-center">{record.invoiceCount}</td>
                                    <td className="text-end">{FormatCurrency(record.totalInvoiced)}</td>
                                    <td>
                                        {record.lastInvoiceDate ? moment(record.lastInvoiceDate).format("ll") : "—"}
                                    </td>
                                    <td className="text-end">
                                        {record.unpaidAmount > 0 ? (
                                            <span className="text-danger fw-semibold">{FormatCurrency(record.unpaidAmount)}</span>
                                        ) : "—"}
                                    </td>
                                    <td className="pe-3">
                                        {record.placeId ? (
                                            <span
                                                className="clickable text-primary"
                                                onClick={() => history.push(`/places/${record.placeId}`)}
                                            >
                                                <i className="fa fa-map-marker-alt me-1" />
                                                {record.placeName}
                                            </span>
                                        ) : (
                                            <span className="text-muted small">{t("Not linked")}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!loading && records.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted py-4">
                                        {t("No records found")}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
            {totalPages > 1 && (
                <Card.Footer className="bg-white">
                    <Pagination
                        totalPages={totalPages}
                        currentPage={page}
                        totalRecords={totalRecords}
                        onPageChanged={(newPage: number) => {
                            setPage(newPage)
                            bindData(newPage, filterObj.search)
                        }}
                    />
                </Card.Footer>
            )}
        </Card>
    )
}

export default AgentCustomers
